import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScheduleTemplateItem, ScheduleTemplateLink, ScheduleLinkType } from '@/lib/scheduleUtils';
import { validateTemplateLinkMutation } from '@/lib/scheduleTemplateManagement';
import ScheduleTemplateLagInput from './ScheduleTemplateLagInput';
import ScheduleTemplateGanttView from './ScheduleTemplateGanttView';

export type EditableTemplateLink = ScheduleTemplateLink & { _localId: string };

interface Props {
  items: ScheduleTemplateItem[];
  links: EditableTemplateLink[];
  onChange: (links: EditableTemplateLink[]) => void;
  disabled?: boolean;
}

const ACTIVE_TYPES = ['FS', 'SS'] as const;
type LinkViewMode = 'list' | 'gantt';

function newLocalId(): string {
  return `link-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export default function ScheduleTemplateEditorLinksSection({
  items,
  links,
  onChange,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<LinkViewMode>('list');
  const [showAdd, setShowAdd] = useState(false);
  const [predId, setPredId] = useState('');
  const [succId, setSuccId] = useState('');
  const [linkType, setLinkType] = useState<ScheduleLinkType>('FS');
  const [lagDays, setLagDays] = useState(0);
  const [taskSearch, setTaskSearch] = useState('');
  const [linkSearch, setLinkSearch] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const itemById = useMemo(() => {
    const map = new Map<string, ScheduleTemplateItem>();
    items.forEach((item) => {
      if (item.id) map.set(item.id, item);
    });
    return map;
  }, [items]);

  const idToLabel = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach((item) => {
      if (item.id) {
        map.set(item.id, `${item.sort_order}. ${item.title || t('templates.untitledTask')}`);
      }
    });
    return map;
  }, [items, t]);

  const filteredItems = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    return items.filter((item) => {
      if (!item.id) return false;
      if (!q) return true;
      return `${item.sort_order} ${item.title}`.toLowerCase().includes(q);
    });
  }, [items, taskSearch]);

  const filteredLinks = useMemo(() => {
    const q = linkSearch.trim().toLowerCase();
    if (!q) return links;
    return links.filter((link) => {
      const pred = itemById.get(link.predecessor_item_id);
      const succ = itemById.get(link.successor_item_id);
      const haystack = `${pred?.sort_order ?? ''} ${pred?.title ?? ''} ${succ?.sort_order ?? ''} ${succ?.title ?? ''} ${link.link_type} ${link.lag_days}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [links, linkSearch, itemById]);

  const groupedLinks = useMemo(() => {
    const groups = new Map<string, EditableTemplateLink[]>();
    for (const link of filteredLinks) {
      const key = link.predecessor_item_id;
      const arr = groups.get(key) ?? [];
      arr.push(link);
      groups.set(key, arr);
    }

    return Array.from(groups.entries())
      .map(([groupPredId, groupLinks]) => ({
        predId: groupPredId,
        pred: itemById.get(groupPredId),
        links: groupLinks.sort((a, b) => {
          const succA = itemById.get(a.successor_item_id);
          const succB = itemById.get(b.successor_item_id);
          return (succA?.sort_order ?? 0) - (succB?.sort_order ?? 0);
        }),
      }))
      .sort((a, b) => (a.pred?.sort_order ?? 0) - (b.pred?.sort_order ?? 0));
  }, [filteredLinks, itemById]);

  const openAddLinkForm = (prefill?: { predecessorId?: string; successorId?: string }) => {
    setShowAdd(true);
    setFormError(null);
    setPredId(prefill?.predecessorId ?? '');
    setSuccId(prefill?.successorId ?? '');
    setLinkType('FS');
    setLagDays(0);
    setTaskSearch('');
    setViewMode('list');
  };

  const handleAddLink = () => {
    setFormError(null);
    const validation = validateTemplateLinkMutation({
      predecessorId: predId,
      successorId: succId,
      links,
      idToLabel,
    });
    if (!validation.ok) {
      setFormError(validation.error);
      return;
    }

    onChange([
      ...links,
      {
        id: '',
        template_id: items[0]?.template_id || '',
        predecessor_item_id: predId,
        successor_item_id: succId,
        link_type: linkType,
        lag_days: lagDays,
        _localId: newLocalId(),
      },
    ]);
    setShowAdd(false);
    setPredId('');
    setSuccId('');
    setLinkType('FS');
    setLagDays(0);
    setTaskSearch('');
  };

  const handleRemove = (localId: string) => {
    onChange(links.filter((l) => l._localId !== localId));
  };

  const handleUpdateLag = (localId: string, lag: number, type: ScheduleLinkType) => {
    onChange(
      links.map((l) =>
        l._localId === localId ? { ...l, lag_days: lag, link_type: type } : l
      )
    );
  };

  const toggleGroup = (groupPredId: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupPredId)) next.delete(groupPredId);
      else next.add(groupPredId);
      return next;
    });
  };

  if (items.filter((i) => i.id).length < 2) {
    return (
      <div className="bg-[#F9FAFB] rounded-xl border border-gray-100 px-4 py-3 text-sm text-[#9CA3AF]">
        {t('templates.linksNeedTwoTasks')}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-bold text-[#374151] uppercase tracking-wider">
          {t('templates.templateLinksHeading', { count: links.length })}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden bg-white">
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap ${
                viewMode === 'list'
                  ? 'bg-[#FEF0E7] text-[#E8621A]'
                  : 'text-[#6B7280] hover:bg-gray-50'
              }`}
            >
              <i className="ri-list-unordered mr-1" />
              {t('templates.listView')}
            </button>
            <button
              type="button"
              onClick={() => setViewMode('gantt')}
              className={`px-3 py-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap border-l border-gray-200 ${
                viewMode === 'gantt'
                  ? 'bg-[#FEF0E7] text-[#E8621A]'
                  : 'text-[#6B7280] hover:bg-gray-50'
              }`}
            >
              <i className="ri-bar-chart-horizontal-line mr-1" />
              {t('templates.ganttView')}
            </button>
          </div>
          <button
            type="button"
            disabled={disabled}
            onClick={() => openAddLinkForm()}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#FEF0E7] text-[#E8621A] text-xs font-semibold cursor-pointer"
          >
            <i className="ri-add-line" />
            {t('templates.addLink')}
          </button>
        </div>
      </div>

      {viewMode === 'list' && links.length > 0 && (
        <input
          type="search"
          value={linkSearch}
          onChange={(e) => setLinkSearch(e.target.value)}
          placeholder={t('templates.searchLinks')}
          className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A]"
        />
      )}

      {showAdd && (
        <div className="bg-[#F9FAFB] rounded-xl border border-gray-100 p-3 space-y-3">
          <input
            type="search"
            value={taskSearch}
            onChange={(e) => setTaskSearch(e.target.value)}
            placeholder={t('templates.searchItems')}
            className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm"
          />
          <div className="grid sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-[#374151] mb-1">{t('templates.predecessor')}</label>
              <select
                value={predId}
                disabled={disabled}
                onChange={(e) => setPredId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm"
              >
                <option value="">{t('templates.chooseTask')}</option>
                {filteredItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.sort_order}. {item.title || t('templates.untitledTask')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#374151] mb-1">{t('templates.successor')}</label>
              <select
                value={succId}
                disabled={disabled}
                onChange={(e) => setSuccId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm"
              >
                <option value="">{t('templates.chooseTask')}</option>
                {filteredItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.sort_order}. {item.title || t('templates.untitledTask')}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-xs font-semibold text-[#374151] mb-1">{t('templates.linkTypeLabel')}</label>
              <div className="grid grid-cols-2 gap-2">
                {ACTIVE_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    disabled={disabled}
                    onClick={() => setLinkType(type)}
                    className={`py-2 rounded-xl border-2 text-xs font-bold cursor-pointer ${
                      linkType === type
                        ? 'border-[#E8621A] bg-[#FEF0E7] text-[#E8621A]'
                        : 'border-gray-100 text-[#374151]'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#374151] mb-1">{t('templates.lagLabel')}</label>
              <ScheduleTemplateLagInput
                value={lagDays}
                disabled={disabled}
                onChange={setLagDays}
                className="min-w-[4.5rem] w-24 px-3 py-2 rounded-xl border border-gray-200 text-sm text-right tabular-nums font-medium text-[#1A1A1A] focus:outline-none focus:border-[#E8621A]"
              />
            </div>
          </div>
          {formError && (
            <p className="text-xs text-red-600">{formError}</p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={handleAddLink}
              className="px-3 py-1.5 rounded-lg bg-[#E8621A] text-white text-xs font-semibold cursor-pointer"
            >
              {t('templates.addDependency')}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#374151] text-xs font-semibold cursor-pointer"
            >
              {t('btn.cancel')}
            </button>
          </div>
        </div>
      )}

      {viewMode === 'gantt' ? (
        <ScheduleTemplateGanttView
          items={items}
          links={links}
          onChange={onChange}
          disabled={disabled}
          itemById={itemById}
          onOpenAddLink={openAddLinkForm}
        />
      ) : links.length === 0 ? (
        <p className="text-sm text-[#9CA3AF]">{t('templates.noLinksYet')}</p>
      ) : groupedLinks.length === 0 ? (
        <p className="text-sm text-[#9CA3AF]">{t('templates.noLinkSearchResults')}</p>
      ) : (
        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
          {groupedLinks.map(({ predId: groupPredId, pred, links: groupLinks }) => {
            const collapsed = collapsedGroups.has(groupPredId);
            return (
              <div key={groupPredId} className="border border-gray-100 rounded-xl overflow-hidden bg-white">
                <button
                  type="button"
                  onClick={() => toggleGroup(groupPredId)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left bg-[#F9FAFB] hover:bg-gray-50 cursor-pointer"
                >
                  <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF]`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">
                      {t('templates.predecessor')}
                    </p>
                    <p className="text-xs font-bold text-[#1A1A1A] truncate">
                      {pred ? `${pred.sort_order}. ${pred.title || t('templates.untitledTask')}` : t('templates.unknownTask')}
                    </p>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A] flex-shrink-0">
                    {t('templates.linkCountBadge', { count: groupLinks.length })}
                  </span>
                </button>
                {!collapsed && (
                  <div>
                    <div className="hidden sm:grid sm:grid-cols-[minmax(0,1fr)_4.5rem_5.5rem_2rem] gap-2 px-3 py-1.5 border-b border-gray-50 bg-white text-[10px] font-bold text-[#9CA3AF] uppercase">
                      <span>{t('templates.predecessor')} → {t('templates.successor')}</span>
                      <span>{t('templates.linkTypeLabel')}</span>
                      <span>{t('templates.lagLabel')}</span>
                      <span />
                    </div>
                    <div className="divide-y divide-gray-50">
                      {groupLinks.map((link) => {
                        const succ = itemById.get(link.successor_item_id);
                        return (
                          <div
                            key={link._localId}
                            className="grid sm:grid-cols-[minmax(0,1fr)_4.5rem_5.5rem_2rem] gap-2 px-3 py-2 items-center"
                          >
                            <div className="min-w-0 text-xs text-[#374151]">
                              <span className="text-[#9CA3AF] mr-1 hidden sm:inline">→</span>
                              <span className="font-semibold">
                                {succ ? `${succ.sort_order}. ${succ.title || t('templates.untitledTask')}` : t('templates.unknownTask')}
                              </span>
                            </div>
                            <select
                              value={link.link_type}
                              disabled={disabled}
                              onChange={(e) =>
                                handleUpdateLag(link._localId, link.lag_days, e.target.value as ScheduleLinkType)
                              }
                              className="px-2 py-1.5 rounded-lg border border-gray-200 text-xs w-full"
                              aria-label={t('templates.linkTypeLabel')}
                            >
                              <option value="FS">FS</option>
                              <option value="SS">SS</option>
                            </select>
                            <ScheduleTemplateLagInput
                              value={link.lag_days}
                              disabled={disabled}
                              onChange={(lag) => handleUpdateLag(link._localId, lag, link.link_type)}
                              title={t('templates.lagDays')}
                              aria-label={t('templates.lagLabel')}
                            />
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => handleRemove(link._localId)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-500 cursor-pointer justify-self-end"
                            >
                              <i className="ri-delete-bin-line text-sm" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
